import React from 'react'
import { View, StyleSheet, useWindowDimensions } from 'react-native'
import { Text, Button, Card, Icon, Spinner, useTheme } from '@ui-kitten/components'

const StatsCards = ({ 
  stats, 
  isLoading, 
  selectedDateRange, 
  onDateRangeChange,
  title,
  dateRangePresets,
  layout // 'vertical' or 'horizontal'
}) => {
  const theme = useTheme()
  const { width } = useWindowDimensions()
  const effectiveLayout = layout ?? (width >= 600 ? 'horizontal' : 'vertical')

  return (
    <View style={styles.statsContainer}>
      <View style={styles.statsHeader}>
        <Text category='h6' style={styles.statsTitle}>
          {title}
        </Text>
        {dateRangePresets && (
          <View style={styles.dateRangeButtons}>
            {dateRangePresets.map((preset) => (
              <Button
                key={preset.key}
                size='small'
                appearance={'outline'}
                status={selectedDateRange === preset.key ? 'primary' : 'basic'}
                onPress={() => onDateRangeChange(preset.key)}
                style={styles.dateRangeButton}
              >
                {preset.label}
              </Button>
            ))}
          </View>
        )}
      </View>
      
      {isLoading ? (
        <View style={styles.statsLoading}>
          <Spinner size='small' />
        </View>
      ) : (
        <View style={[styles.statsCards, effectiveLayout === 'horizontal' && styles.statsCardsHorizontal]}>
          {stats.map((stat, index) => (
            <Card key={index} style={[styles.statCard, effectiveLayout === 'horizontal' && styles.statCardHorizontal]}>
              <View style={[styles.statCardContent, effectiveLayout === 'horizontal' && styles.statCardContentHorizontal]}>
                <View style={styles.statCardLeft}>
                  <View style={styles.statIcon}>
                    <Icon name={stat.icon} width={effectiveLayout === 'horizontal' ? 24 : 28} height={effectiveLayout === 'horizontal' ? 24 : 28} fill={stat.color} />
                  </View>
                  <View style={styles.statTextContainer}>
                    <Text style={[styles.statLabel, { color: theme['text-hint-color'] }, effectiveLayout === 'horizontal' && styles.statLabelHorizontal]}>{stat.label}</Text>
                  </View>
                </View>
                <View style={styles.statCardRight}>
                  <Text style={[styles.statValue, { color: theme['text-basic-color'] }, effectiveLayout === 'horizontal' && styles.statValueHorizontal]}>{stat.value}</Text>
                </View>
              </View>
            </Card>
          ))}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  statsContainer: {
    margin: 16,
    marginBottom: 8,
  },
  statsHeader: {
    marginBottom: 12,
  },
  statsTitle: {
    marginBottom: 12,
    fontWeight: 'bold',
    fontSize: 18,
  },
  dateRangeButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  dateRangeButton: {
    marginRight: 6,
    marginBottom: 6,
    minHeight: 32,
    paddingHorizontal: 12,
  },
  statsLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  statsCards: {
    flexDirection: 'column',
    gap: 8,
  },
  statCard: {
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  statCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  statIcon: {
    marginRight: 12,
  },
  statTextContainer: {
    flex: 1,
  },
  statLabel: {
    fontSize: 14,
    lineHeight: 18,
  },
  statCardRight: {
    alignItems: 'flex-end',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 28,
  },
  // Horizontal layout styles
  statsCardsHorizontal: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statCardHorizontal: {
    minWidth: 140,
    flex: 1,
    marginBottom: 8,
    padding: 12,
  },
  statCardContentHorizontal: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  statLabelHorizontal: {
    fontSize: 12,
    marginBottom: 4,
  },
  statValueHorizontal: {
    fontSize: 20,
    textAlign: 'center',
  },
})

export default StatsCards
